import { useState, useEffect } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import api from "../../../configs/api";
import defaultAvatar from "../../../assets-user/agents/default_avatar.png";
import verifiedLogo from "../../../assets-user/agents/VerifiedBadge.png";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const PreferredAgents = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await api.get("/agent-builder");
        const list = res.data?.list || [];
        setAgents(list.slice(0, 6));
      } catch (error) {
        console.error("Failed fetching agents:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, []);

  return (
    <div className="w-full px-4 sm:px-6 md:px-8 lg:px-10  bg-white ">
      <div className="mx-auto">
        {/* HEADER */}
        <div className="flex justify-between items-center pb-6 md:pb-8 lg:pb-10">
          <div className="font-medium flex flex-col md:flex-row md:gap-2 text-[#031722] text-lg sm:text-xl md:text-2xl">
            <span>LeadBook Preferred</span>
            <span>Agents in Bhubaneswar</span>
          </div>

          <button
            onClick={() => {
              navigate("/agents");
              window.scrollTo(0, 0);
            }}
            className="flex items-center self-end gap-1 sm:gap-2 text-red-600 font-medium text-xs sm:text-base"
          >
            View all <ArrowRight className="w-3 sm:w-4 h-3 sm:h-4" />
          </button>
        </div>

        {/* SLIDER / GRID */}
        <div className="overflow-x-auto lg:overflow-hidden scroll-smooth pb-6 md:pb-8 lg:pb-10">
          <div className="flex md:flex lg:grid gap-4 sm:gap-5 lg:gap-6 lg:grid-cols-3">
            {loading
              ? Array(6)
                  .fill(0)
                  .map((_, i) => <AgentSkeleton key={i} />)
              : agents.map((agent, index) => {
                  return <AgentCard key={agent.id || index} agent={agent} />;
                })}
          </div>
        </div>
      </div>
    </div>
  );
};

const AgentCard = ({ agent }) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-lg shadow-sm border-border border overflow-hidden flex-shrink-0 w-[80%] sm:w-[70%] md:w-[60%] lg:w-auto  transition-all duration-200 hover:shadow-xl ">
      {/* TOP CARD AREA */}
      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="flex-shrink-0">
            <div className="relative flex flex-col items-center">
              <img
                src={agent.image || defaultAvatar}
                alt={agent.name}
                className="w-14 h-14 lg:w-16 lg:h-16 rounded-full object-cover"
              />

              {agent.isVerified && (
                <img
                  src={verifiedLogo}
                  alt="Verified"
                  className=" w-16 object-contain mt-1"
                />
              )}
            </div>
          </div>

          {/* CENTER DETAILS */}
          <div className="flex-1 min-w-0 flex flex-col tracking-wide">
            <h3 className="font-medium text-[#031722] text-base sm:text-lg md:text-xl lg:text-[18.7px] mb-1 line-clamp-1">
              {agent.name}
            </h3>

            <p className="text-xs sm:text-sm line-clamp-1 text-gray-700 mb-1">
              {agent.role}
            </p>

            {/* LOCATION IF EXISTS */}
            {agent.location && (
              <p className="flex items-center mt-1 text-xs sm:text-sm text-gray-700">
                <FaMapMarkerAlt className=" mr-1 text-gray-400" />
                <span className="line-clamp-1 ">{agent.location}</span>
              </p>
            )}

            <button
              onClick={() => {
                navigate(`/agents/${agent._id}`);
                window.scrollTo(0, 0);
              }}
              className="px-3 sm:px-4 py-1.5 self-end mt-3 bg-red-600 text-white rounded-full text-[0.7rem] sm:text-sm font-semiboldtransition-all duration-200 active:scale-95 hover:bg-red-700"
            >
              View Details
            </button>
          </div>
        </div>
      </div>

      {/* BOTTOM SECTION */}
      <div className="border-t border-gray-200 bg-[#F4E3D9] px-4 sm:px-5 lg:px-2 xl:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* SHOW COMPANY LOGO IF EXISTS */}
          {agent.worksWithCompany ? (
            <div className="flex-shrink-0">
              <img
                src={agent.logoImg}
                alt="Company"
                className="w-14 h-14 lg:w-16 lg:h-16 object-cover rounded-lg overflow-hidden"
              />
            </div>
          ) : (
            <div className="w-14 h-14 lg:w-16 lg:h-16"></div>
          )}

          {/* STATS */}
          <div className={`gap-4 lg:gap-10 flex-1 pl-4 grid grid-cols-3`}>
            <StatCount
              label="Properties for Sale"
              value={agent.propertyForSale}
            />
            <StatCount label="Deals Closed" value={agent.dealsClosed} />
            {agent.worksWithCompany && (
              <StatCount label="Team Members" value={agent.teamMembers} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AgentSkeleton = () => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-border overflow-hidden flex-shrink-0 w-[80%] sm:w-[70%] md:w-[60%] lg:w-auto animate-pulse">
      {/* TOP */}
      <div className="p-4 sm:p-5 lg:p-6 space-y-4">
        <div className="flex items-start gap-3 sm:gap-4">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-[#FDECE4]" />
          </div>

          {/* Text lines */}
          <div className="flex-1 space-y-2">
            <div className="h-4 sm:h-5 w-2/3 bg-[#FDECE4] rounded" />

            <div className="h-3 sm:h-4 w-1/2 bg-[#FDECE4] rounded" />

            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-[#FDECE4]" />
              <div className="h-3 w-32 bg-[#FCE1D6] rounded" />
            </div>
          </div>
        </div>

        {/* Button */}
        <div className="flex justify-end">
          <div className="w-20 sm:w-28 h-7 bg-[#FDECE4] rounded-full" />
        </div>
      </div>

      {/* BOTTOM */}
      <div className="border-t border-gray-200 bg-[#F4E3D9] px-4 sm:px-5 lg:px-2 xl:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex-shrink-0">
            <div className="w-14 h-14 lg:w-16 lg:h-16 bg-[#FDECE4] rounded-lg" />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 lg:gap-10 flex-1 pl-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-1">
                <div className="h-4 w-10 bg-[#FDECE4] rounded" />
                <div className="h-3 w-20 bg-[#FCE1D6] rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCount = ({ label, value }) => (
  <div>
    <div className="font-medium text-base lg:text-lg">{value}</div>
    <div className="text-[0.6rem] lg:text-xs text-text-secondary">{label}</div>
  </div>
);

export default PreferredAgents;
